import React, { useEffect, useState } from 'react'
import Layout from '../../components/Layout/Layout'
import DatePicker from 'react-date-picker'
import { DocumentDownloadIcon, QuestionMarkCircleIcon, SearchIcon } from '@heroicons/react/solid';
import { convert, formatDate } from '../../utils/convertDate';
import GeneralTable from '../../components/GeneralTable';
import { useNavigate } from 'react-router-dom';
import WakanowServices from '../../services/wakanowServices';
import { FormatMoney } from '../../utils/formatMoney';

const oldFrom = new Date().setDate(new Date().getDate() - 7);
const longNew = formatDate(new Date());
const defaultFrom = formatDate(new Date().setDate(new Date().getDate() - 7))

const statusColors = {
    'successful': 'bg-green-100 text-green-700',
    'initiated': 'bg-yellow-100 text-yellow-700',
    'failed': 'bg-red-100 text-red-700',
};

const statusBadge = (status) => (
    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full shadow-sm ${statusColors[String(status).toLowerCase()] || 'bg-gray-100 text-gray-600'}`}>
        {status || 'N/A'}
    </span>
);

const confirmationBadge = (confirmation) => (
    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full shadow-sm ${confirmation === 'Success' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
        {confirmation || 'Pending'}
    </span>
);

const columns = [
    { key: 'bookingId', header: 'Booking ID' },
    { key: 'sessionEmail', header: 'Email' },
    { key: 'amount', header: 'Amount', render: (item) => FormatMoney(item.amount, "NGN") },
    { key: 'bookingStatus', header: 'Status', render: (item) => statusBadge(item.bookingStatus) },
    { key: 'hotelBookingConfirmation', header: 'Confirmation', render: (item) => confirmationBadge(item.hotelBookingConfirmation) },
    { key: 'paymentStatus', header: 'Payment', render: (item) => item.paymentStatus || 'N/A' },
    { key: 'transferReference', header: 'Transfer Ref', render: (item) => item.transferReference || 'N/A' },
    { key: 'dateCreated', header: 'Date Created', render: (item) => (item.dateCreated ? convert(item.dateCreated) : 'N/A') },
];

const getHotelActions = () => ['View Booking'];

const HotelBookings = () => {
    const navigate = useNavigate()

    const [search, setSearch] = useState("")
    const [bookingStatus, setBookingStatus] = useState("")
    const [too, setToo] = useState(convert(longNew));
    const [fromm, setFromm] = useState(convert(defaultFrom));
    const [formValues, setFormValues] = useState({
        from: new Date(oldFrom),
        to: new Date()
    });

    const [filters, setFilters] = useState({
        search: '',
        status: '',
        fromm: convert(defaultFrom),
        too: convert(longNew),
    });

    const [pageIndex, setPageIndex] = useState(1);
    const itemsPerPage = 10;

    const [listings, setListings] = useState([]);
    const [totalListingsCount, setTotalListingsCount] = useState(0);
    const [totalPagesCount, setTotalPagesCount] = useState(0);

    const [walletBalance, setWalletBalance] = useState(null);

    const prevPage = () => {
        if (pageIndex > 1) setPageIndex(pageIndex - 1);
    };

    const nextPage = () => {
        if (pageIndex < totalPagesCount) setPageIndex(pageIndex + 1);
    }

    const GetListings = async (page, pageSize, searchTerm, status, startDate, endDate) => {
        try {
            const response = await WakanowServices.listHotelBookings(page, pageSize, searchTerm, status, startDate, endDate);
            setListings(response.data.result);
            setTotalListingsCount(response.data.totalCount);
            setTotalPagesCount(Math.ceil(response.data.totalCount / itemsPerPage));
        } catch (error) {
            setListings([]);
            setTotalListingsCount(0);
            setTotalPagesCount(0);
        }
    }

    const GetWalletBalance = async () => {
        try {
            const response = await WakanowServices.getWalletBalance();
            if (response) {
                setWalletBalance(response.data);
            }
        } catch (error) { }
    }

    useEffect(() => {
        GetListings(pageIndex, itemsPerPage, filters.search, filters.status, filters.fromm, filters.too);
    }, [pageIndex, filters])

    useEffect(() => {
        GetWalletBalance();
    }, [])

    const range = async (e) => {
        e.preventDefault();
        setPageIndex(1);
        setFilters({ search, status: bookingStatus, fromm, too });
    }

    const handleActionSelect = async (action, item) => {
        if (action === "View Booking") {
            navigate(`/wakanow/hotel-bookings/${item?.hotelBookingId}`);
        }
    };

    const clearFilters = () => {
        setSearch("");
        setBookingStatus("");
        setFormValues({ from: new Date(oldFrom), to: new Date() });
        setFromm(convert(defaultFrom));
        setToo(convert(longNew));
        setPageIndex(1);
        setFilters({
            search: '',
            status: '',
            fromm: convert(defaultFrom),
            too: convert(longNew),
        });
    };

    const dateValidation = {
        fromHandler: (event) => {
            setFormValues({ ...formValues, from: event });
            setFromm(event ? convert(formatDate(event)) : '');
        },
        toHandler: (event) => {
            setFormValues({ ...formValues, to: event });
            setToo(event ? convert(formatDate(event)) : '');
        }
    };

    const exportData = async () => {
        const resp = await WakanowServices.exportHotelBookings(filters.search, filters.status, filters.fromm, filters.too);
        if (resp) {
            window.open(resp.data)
        }
    }

    return (
        <Layout title="Hotel Bookings" pageTag={"wakanow"} current={'/wakanow/hotel-bookings'}>
            <React.Fragment>
                <main className='mt-5'>
                    {walletBalance && (
                        <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-xl shadow-lg p-6 mb-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-emerald-200 text-sm font-medium uppercase tracking-wide">NGN Wallet Balance</p>
                                    <p className="text-3xl font-bold mt-1">{FormatMoney(walletBalance.balance, "NGN")}</p>
                                </div>
                                <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                                    <span className="text-emerald-200 text-sm">{walletBalance.currency || 'NGN'}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="sm:flex items-center flex-wrap">
                        <div className="sm:flex-auto flex items-center flex-wrap">
                            <div className="relative rounded-md shadow-sm">
                                <p className='text-sm leading-3 text-gray-500'>From</p>
                                <div>
                                    <DatePicker onChange={dateValidation.fromHandler} value={formValues.from} />
                                </div>
                            </div>
                            <div className="relative rounded-md shadow-sm ml-3">
                                <p className='text-sm leading-3 text-gray-500'>To</p>
                                <div>
                                    <DatePicker onChange={dateValidation.toHandler} value={formValues.to} />
                                </div>
                            </div>
                            <div className='flex ml-4'>
                                <div className="mt-5 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </div>
                                    <input
                                        type="text"
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                        placeholder="Search bookings"
                                    />
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                        <div className='tooltip'>
                                            <QuestionMarkCircleIcon className="w-4 mr-1.5 text-gray-400" />
                                            <span className="tooltiptext"><p>Search by booking ID or email</p></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='mt-5 ml-4'>
                                <select
                                    value={bookingStatus}
                                    onChange={(e) => setBookingStatus(e.target.value)}
                                    className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md shadow-sm"
                                >
                                    <option value="">All Statuses</option>
                                    <option value="initiated">Initiated</option>
                                    <option value="successful">Successful</option>
                                    <option value="failed">Failed</option>
                                </select>
                            </div>
                            <div className='mt-5 ml-4'>
                                <button
                                    onClick={range}
                                    className='hover:text-white active:text-white inline-flex mr-2 items-center justify-center rounded-md border border-transparent btn-green px-6 py-2 text-sm font-medium text-green shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto'
                                >
                                    Search
                                </button>
                            </div>
                            <div className='mt-5 ml-4'>
                                <button
                                    onClick={clearFilters}
                                    className='inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto'
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center mt-3">
                            <button
                                type="button"
                                onClick={exportData}
                                className="inline-flex mr-2 items-center hover:text-white active:text-white justify-center rounded-md border border-transparent btn-green px-2 py-2 text-sm font-medium text-green shadow-sm sm:w-auto"
                            >
                                <DocumentDownloadIcon className="w-4 mr-1.5" />
                                Export
                            </button>
                        </div>
                    </div>

                    <GeneralTable
                        columns={columns}
                        data={listings}
                        nextPage={nextPage}
                        pageIndex={pageIndex}
                        totalCount={totalListingsCount}
                        totalPages={totalPagesCount}
                        prevPage={prevPage}
                        setPageIndex={setPageIndex}
                        actions={getHotelActions}
                        onActionSelect={handleActionSelect}
                        emptyMessage="No hotel bookings found"
                    />
                </main>
            </React.Fragment>
        </Layout>
    )
}

export default HotelBookings
